import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchMenu } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Reenvía fotos externas (Unsplash/Pexels, etc.) por nuestro propio servidor:
// algunos navegadores de Smart TV no logran conectar por HTTPS directo a esos
// CDNs, aunque sí cargan bien nuestro propio sitio.
const imgUrl = (src) => {
  if (!src || !/^https?:\/\//.test(src) || (BACKEND && src.startsWith(BACKEND))) return src;
  return `${API}/img-proxy?url=${encodeURIComponent(src)}`;
};

const IdleScreen = ({ code }) => (
  <div className="flex h-full w-full flex-col items-center justify-center bg-brand-navydark text-center">
    <p className="font-script text-7xl font-bold text-white">Carlito's <span className="text-brand-red">Cuban Food</span></p>
    <p className="mt-6 text-xl text-white/60">Esta pantalla está lista. Asígnale contenido desde el ERP.</p>
    <p className="mt-3 font-mono text-3xl font-bold tracking-[0.3em] text-brand-red">{code}</p>
  </div>
);

const priceOf = (v) => (v != null ? `$${Number(v).toFixed(2)}` : null);
const CATEGORY_ORDER = ["Cocina", "Línea", "Repostería", "Caja"];

// Las categorías son internas del ERP (Caja/Cocina/Repostería/Línea) y no
// deben mostrarse tal cual al cliente — aquí se traducen a un nombre y una
// frase de venta pensados para la pantalla.
const CATEGORY_DISPLAY = {
  Cocina: { label: "Platos Fuertes", tagline: "Cocinados al momento, con sabor de casa" },
  "Línea": { label: "Sándwiches y Antojos", tagline: "Perfectos para cualquier momento del día" },
  "Repostería": { label: "Postres", tagline: "El toque dulce que no te puedes perder" },
  Caja: { label: "Bebidas", tagline: "Refrescantes, para acompañar tu comida" },
};

// Tablero compartido por "Menú con precios" y "Promoción": panel claro con
// marca + primer ítem en grande a la izquierda, y el resto en cuadrícula a
// la derecha sobre fondo navy. Una promoción es solo un tablero cuyos
// "platillos" son las fotos subidas con su nombre y precio.
const BoardClasico = ({ tag, catName, tagline, items, orientation }) => {
  if (!items || !items.length) return <IdleScreen code="" />;
  const hero = items[0];
  const rest = items.length > 1 ? items.slice(1) : items;

  if (orientation === "vertical") {
    return (
      <div className="relative flex h-full w-full flex-col overflow-hidden bg-brand-navydark">
        <div className="w-full px-8 py-7 text-center" style={{ background: "#FFF9F5" }}>
          <p className="font-script text-2xl font-bold leading-none text-brand-navy">Carlito's</p>
          <p className="mt-1 text-xs font-extrabold uppercase tracking-[0.2em] text-brand-red">Cuban Food</p>
          {tag && <p className="mt-2 font-heading text-[10px] font-extrabold uppercase tracking-widest text-brand-navy/40">{tag}</p>}
          <div className="mt-3 flex items-center justify-center gap-5">
            {hero.image && (
              <div className="h-32 w-32 shrink-0 overflow-hidden rounded-2xl shadow-xl">
                <img src={imgUrl(hero.image)} alt={hero.name} className="h-full w-full object-cover" />
              </div>
            )}
            <div className="text-left">
              <p className="font-heading text-lg font-extrabold uppercase leading-tight text-brand-navy">{hero.name}</p>
              {hero.price != null && <p className="mt-2 inline-block rounded-full bg-brand-red px-5 py-1.5 font-heading text-base font-extrabold text-white">{priceOf(hero.price)}</p>}
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-hidden px-7 py-6">
          <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-white/40">Carlito's Cuban Food</p>
          <p className="mt-1 font-heading text-2xl font-extrabold uppercase tracking-wide text-brand-red">{catName}</p>
          <p className="mb-3 mt-1 border-b-2 border-white/10 pb-3 text-sm font-semibold text-white/60">{tagline}</p>
          <div className="grid grid-cols-2 gap-4">
            {rest.map((it) => (
              <div key={it.id} className="text-center">
                {it.image && <img src={imgUrl(it.image)} alt={it.name} className="h-28 w-full rounded-2xl object-cover shadow-lg" />}
                <p className="mt-2 font-heading text-sm font-bold leading-tight text-white">{it.name}</p>
                {it.price != null && <p className="mt-1 font-heading text-base font-extrabold" style={{ color: "#f0c94f" }}>{priceOf(it.price)}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full overflow-hidden bg-brand-navydark">
      <div
        className="absolute left-0 top-0 h-full w-[27%] px-8 py-10 text-center"
        style={{ background: "#FFF9F5", clipPath: "polygon(0 0, 100% 0, 80% 100%, 0 100%)" }}
      >
        <p className="font-script text-3xl font-bold leading-none text-brand-navy">Carlito's</p>
        <p className="mt-1 text-sm font-extrabold uppercase tracking-[0.2em] text-brand-red">Cuban Food</p>
        {tag && <p className="mt-4 font-heading text-xs font-extrabold uppercase tracking-widest text-brand-navy/40">{tag}</p>}
        {hero.image && (
          <div className="mt-4 overflow-hidden rounded-2xl shadow-xl">
            <img src={imgUrl(hero.image)} alt={hero.name} className="h-56 w-full object-cover" />
          </div>
        )}
        <p className="mt-4 font-heading text-xl font-extrabold uppercase leading-tight text-brand-navy">{hero.name}</p>
        {hero.price != null && <p className="mt-3 inline-block rounded-full bg-brand-red px-6 py-2 font-heading text-2xl font-extrabold text-white">{priceOf(hero.price)}</p>}
      </div>
      <div className="absolute left-[27%] top-0 h-full w-[73%] px-10 py-9">
        <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-white/40">Carlito's Cuban Food</p>
        <p className="mt-1 font-heading text-4xl font-extrabold uppercase tracking-wide text-brand-red">{catName}</p>
        <p className="mb-4 mt-1 border-b-2 border-white/10 pb-4 text-lg font-semibold text-white/60">{tagline}</p>
        <div className="mt-5 grid grid-cols-4 gap-5">
          {rest.map((it) => (
            <div key={it.id} className="text-center">
              {it.image && <img src={imgUrl(it.image)} alt={it.name} className="h-32 w-full rounded-2xl object-cover shadow-lg" />}
              <p className="mt-2 font-heading text-base font-bold leading-tight text-white">{it.name}</p>
              {it.price != null && <p className="mt-1 font-heading text-lg font-extrabold" style={{ color: "#f0c94f" }}>{priceOf(it.price)}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Variante de tablero estilo "flyer" naranja: ítem destacado en tarjeta
// crema con foto redonda, y el resto en fila de círculos con su precio.
const BoardNaranja = ({ tag, catName, tagline, items, orientation }) => {
  if (!items || !items.length) return <IdleScreen code="" />;
  const hero = items[0];
  const rest = items.length > 1 ? items.slice(1) : [];
  const vertical = orientation === "vertical";
  return (
    <div className={`relative flex h-full w-full flex-col overflow-hidden ${vertical ? "px-8 py-8" : "px-16 py-12"}`} style={{ background: "linear-gradient(135deg, #FF8A3D, #FF4D1C)" }}>
      <p className={`font-extrabold uppercase tracking-[0.3em] text-white/80 ${vertical ? "text-xs" : "text-sm"}`}>Carlito's Cuban Food</p>
      <p className={`mt-2 font-heading font-black uppercase leading-none text-white ${vertical ? "text-4xl" : "text-6xl"}`}>
        {tag ? `${tag}: ` : ""}<span style={{ color: "#FFE9A8" }}>{catName}</span>
      </p>
      {tagline && <p className={`mt-2 font-bold text-white/85 ${vertical ? "text-base" : "text-xl"}`}>{tagline}</p>}

      <div className={`flex flex-1 items-center gap-10 ${vertical ? "mt-6" : "mt-10"}`}>
        <div className={`flex w-full items-center rounded-[28px] shadow-2xl ${vertical ? "max-w-full flex-col gap-5 px-6 py-8 text-center" : "max-w-3xl gap-8 px-10 py-10"}`} style={{ background: "#FFF3DC" }}>
          {hero.image && (
            <div className={`shrink-0 overflow-hidden rounded-full border-[6px] border-white shadow-xl ${vertical ? "h-40 w-40" : "h-52 w-52"}`}>
              <img src={imgUrl(hero.image)} alt={hero.name} className="h-full w-full object-cover" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className={`font-heading font-black uppercase leading-tight ${vertical ? "text-2xl" : "text-4xl"}`} style={{ color: "#7a2b00" }}>{hero.name}</p>
            {hero.price != null && (
              <span className={`mt-4 inline-block rounded-full font-heading font-black text-white shadow ${vertical ? "px-5 py-1.5 text-xl" : "px-6 py-2 text-2xl"}`} style={{ background: "#FF4D1C" }}>{priceOf(hero.price)}</span>
            )}
          </div>
        </div>
      </div>

      {rest.length > 0 && (
        <div className="mt-6 flex flex-wrap justify-center gap-7">
          {rest.map((it) => (
            <div key={it.id} className="flex flex-col items-center">
              <div className={`overflow-hidden rounded-full border-4 border-white/80 shadow-lg ${vertical ? "h-20 w-20" : "h-24 w-24"}`}>
                {it.image && <img src={imgUrl(it.image)} alt={it.name} className="h-full w-full object-cover" />}
              </div>
              <p className="mt-2 font-heading text-sm font-extrabold uppercase text-white">{it.name}</p>
              {it.price != null && <span className="mt-1 rounded-full px-3 py-0.5 text-xs font-black" style={{ background: "#7a2b00", color: "#FFE9A8" }}>{priceOf(it.price)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Variante "combo" estilo menú de comida rápida: columnas numeradas con la
// foto a toda altura y el precio abajo. Pensada para sándwiches, pizza, etc.
// Se limita a 5 columnas — con más ítems se ven apretadas; para listas largas
// conviene el diseño Clásico o filtrar categorías al agregar el ítem.
const COMBO_BG = ["#0B1F4B", "#C8102E"];
const BoardCombo = ({ catName, tagline, items, orientation }) => {
  if (!items || !items.length) return <IdleScreen code="" />;
  const cols = items.slice(0, 5);
  const vertical = orientation === "vertical";

  const header = (
    <div className="flex items-center justify-between bg-black/60 px-6 py-3">
      <p className="font-script text-xl font-bold text-white">Carlito's <span className="text-brand-red">Cuban Food</span></p>
      <div className="text-right">
        <p className="font-heading text-lg font-extrabold uppercase tracking-wide text-white">{catName}</p>
        {tagline && <p className="text-xs font-semibold text-white/70">{tagline}</p>}
      </div>
    </div>
  );

  if (vertical) {
    return (
      <div className="relative flex h-full w-full flex-col overflow-hidden bg-black">
        {header}
        <div className="flex flex-1 flex-col">
          {cols.map((it, i) => (
            <div key={it.id} className="relative flex flex-1 items-stretch border-b-4 border-black/30 last:border-b-0" style={{ background: `linear-gradient(165deg, ${COMBO_BG[i % 2]}, #000)` }}>
              <div className="flex w-24 shrink-0 flex-col items-center justify-center">
                <p className="font-heading text-[10px] font-extrabold uppercase tracking-widest text-white/50">Platillo</p>
                <p className="font-heading text-4xl font-black leading-none text-white/90">{i + 1}</p>
              </div>
              <div className="relative flex-1 overflow-hidden">
                {it.image && (
                  <img
                    src={imgUrl(it.image)}
                    alt={it.name}
                    className="h-full w-full object-cover"
                    style={{ animation: "combozoom 9s ease-in-out infinite alternate", animationDelay: `${i * 0.6}s` }}
                  />
                )}
              </div>
              <div className="flex w-36 shrink-0 flex-col justify-center bg-black/60 px-3">
                <p className="font-heading text-sm font-black uppercase leading-tight text-white">{it.name}</p>
                {it.price != null && <p className="mt-1 font-heading text-lg font-black" style={{ color: "#FFE9A8" }}>{priceOf(it.price)}</p>}
              </div>
            </div>
          ))}
        </div>
        <style>{`@keyframes combozoom { from { transform: scale(1.04); } to { transform: scale(1.14); } }`}</style>
      </div>
    );
  }

  return (
    <div className="relative flex h-full w-full overflow-hidden bg-black">
      <div className="absolute inset-x-0 top-0 z-10">{header}</div>
      {cols.map((it, i) => (
        <div key={it.id} className="relative flex flex-1 flex-col border-r-4 border-black/30 last:border-r-0" style={{ background: `linear-gradient(165deg, ${COMBO_BG[i % 2]}, #000)` }}>
          <p className="pt-24 text-center font-heading text-sm font-extrabold uppercase tracking-widest text-white/50">Platillo</p>
          <p className="text-center font-heading text-7xl font-black leading-none text-white/90">{i + 1}</p>
          <div className="relative mt-2 flex-1 overflow-hidden">
            {it.image && (
              <img
                src={imgUrl(it.image)}
                alt={it.name}
                className="h-full w-full object-cover"
                style={{ animation: "combozoom 9s ease-in-out infinite alternate", animationDelay: `${i * 0.6}s` }}
              />
            )}
          </div>
          <div className="bg-black/60 px-4 py-4">
            <p className="font-heading text-xl font-black uppercase leading-tight text-white">{it.name}</p>
            {it.price != null && <p className="mt-1 font-heading text-3xl font-black" style={{ color: "#FFE9A8" }}>{priceOf(it.price)}</p>}
          </div>
        </div>
      ))}
      <style>{`@keyframes combozoom { from { transform: scale(1.04); } to { transform: scale(1.14); } }`}</style>
    </div>
  );
};

const chunk = (arr, n) => Array.from({ length: Math.ceil(arr.length / n) }, (_, i) => arr.slice(i * n, i * n + n));

// Reparte en páginas parejas (13 → 7 + 6, no 12 + 1).
const evenChunks = (arr, max) => (arr.length ? chunk(arr, Math.ceil(arr.length / Math.ceil(arr.length / max))) : []);

// Lienzo fijo de 1920×1080 (o 1080×1920 vertical) escalado al marco real: los
// diseños nuevos usan medidas fijas y así se ven idénticos en un TV 4K, en
// uno cuyo navegador reporta 1280×720 o en la vista previa del ERP.
const FrameContext = createContext({ w: 1920, h: 1080 });

const Stage = ({ vertical, style, children }) => {
  const { w, h } = useContext(FrameContext);
  const W = vertical ? 1080 : 1920;
  const H = vertical ? 1920 : 1080;
  const s = Math.min(w / W, h / H);
  return (
    <div className="relative h-full w-full overflow-hidden" style={style}>
      <div style={{ position: "absolute", left: (w - W * s) / 2, top: (h - H * s) / 2, width: W, height: H, transform: `scale(${s})`, transformOrigin: "0 0" }}>
        {children}
      </div>
    </div>
  );
};

// Diseño "Comida rápida": pared de menú estilo fast food. Los platillos con
// foto van en tarjetas de colores (6 por pantalla); los que aún no tienen
// foto, en tarjetas de texto o en páginas de lista de precios. Solo anima
// transform/opacity para no trabar TVs modestos.
const FF_COLORS = ["#E3001B", "#1F1574", "#F26A1B", "#7A0BC0", "#0B7A3E", "#C2185B"];
const FF_PER_PAGE = 6;
const FF_LIST_PER_PAGE = { horizontal: 12, vertical: 10 };
const FF_BG = {
  backgroundColor: "#110B3D",
  backgroundImage: "repeating-linear-gradient(135deg, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 24px, rgba(255,255,255,0) 24px, rgba(255,255,255,0) 48px)",
};
const FF_STRIPES = "repeating-linear-gradient(135deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 22px, rgba(255,255,255,0) 22px, rgba(255,255,255,0) 44px)";
const clampLines = (n) => ({ display: "-webkit-box", WebkitLineClamp: n, WebkitBoxOrient: "vertical", overflow: "hidden" });
const FF_CSS = `
@keyframes ffPop { 0% { opacity: 0; transform: translateY(40px) scale(.9); } 100% { opacity: 1; transform: none; } }
@keyframes ffZoom { from { transform: scale(1); } to { transform: scale(1.09); } }
@keyframes ffPulse { 0%, 100% { transform: rotate(-4deg) scale(1); } 50% { transform: rotate(-4deg) scale(1.1); } }
@keyframes ffShine { 0% { transform: translateX(-160%) skewX(-20deg); } 55%, 100% { transform: translateX(420%) skewX(-20deg); } }
@keyframes ffHeader { 0% { opacity: 0; transform: translateY(-30px); } 100% { opacity: 1; transform: none; } }
.ff-pop { animation: ffPop .65s cubic-bezier(.2,.9,.3,1.25) both; }
.ff-zoom { animation: ffZoom 9s ease-in-out infinite alternate; }
.ff-price { animation: ffPulse 2.2s ease-in-out infinite; }
.ff-shine { position: absolute; top: 0; bottom: 0; left: 0; width: 30%; pointer-events: none; background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.38), rgba(255,255,255,0)); animation: ffShine 7s ease-in-out infinite; }
.ff-header { animation: ffHeader .6s ease-out both; }
`;
// Alto de la cabecera y margen inferior (para los puntitos) del lienzo.
const FF_HEADER_H = { horizontal: 212, vertical: 290 };
const FF_BOTTOM = 64;

const FFHeader = ({ catName, tagline, vertical }) => (
  <div className={`ff-header flex shrink-0 items-end justify-between ${vertical ? "px-12" : "px-14"}`} style={{ height: FF_HEADER_H[vertical ? "vertical" : "horizontal"], paddingBottom: 24 }}>
    <div>
      <p className="font-script font-bold leading-none text-white" style={{ fontSize: 44 }}>Carlito's <span className="text-[#FFD23F]">Cuban Food</span></p>
      <p className="mt-3 font-heading font-black uppercase leading-none text-white" style={{ fontSize: vertical ? 84 : 92 }}>{catName}</p>
      {tagline && vertical && <p className="mt-4 font-heading font-bold text-[#FFD23F]" style={{ fontSize: 30 }}>{tagline}</p>}
    </div>
    {tagline && !vertical && <p className="pb-2 text-right font-heading font-bold text-[#FFD23F]" style={{ fontSize: 30, maxWidth: 620 }}>{tagline}</p>}
  </div>
);

const FFPrice = ({ price, size = 34, delay = 0 }) => (price != null ? (
  <span className="ff-price shrink-0 rounded-2xl bg-[#FFD23F] px-4 py-2 font-heading font-black leading-none text-[#1F1574] shadow-lg" style={{ fontSize: size, animationDelay: `${delay}s` }}>{priceOf(price)}</span>
) : null);

const FFDots = ({ count, active }) => (
  <div className="absolute left-0 right-0 flex justify-center" style={{ bottom: 24 }}>
    {Array.from({ length: count }, (_, k) => (
      <span key={k} className="mx-1.5 h-2.5 rounded-full" style={{ width: k === active ? 36 : 10, backgroundColor: k === active ? "#FFD23F" : "rgba(255,255,255,0.3)" }} />
    ))}
  </div>
);

const FFCard = ({ it, i, color }) => (
  <div className="ff-pop relative flex min-h-0 flex-col overflow-hidden rounded-[28px] shadow-2xl" style={{ backgroundColor: color, animationDelay: `${i * 0.1}s` }}>
    {it.image ? (
      <>
        <div className="relative min-h-0 flex-1 overflow-hidden">
          <img src={imgUrl(it.image)} alt={it.name} className="ff-zoom h-full w-full object-cover" style={{ animationDelay: `${-i * 1.5}s` }} />
          {it.popular && <span className="absolute left-4 top-4 rounded-full bg-[#FFD23F] px-4 py-1.5 font-heading text-base font-black uppercase tracking-wider text-[#1F1574] shadow-lg">★ Favorito</span>}
        </div>
        <div className="flex shrink-0 items-center justify-between px-5 py-4">
          <p className="pr-3 font-heading text-[28px] font-black uppercase leading-tight text-white" style={clampLines(2)}>{it.name}</p>
          <FFPrice price={it.price} delay={i * 0.35} />
        </div>
      </>
    ) : (
      // Sin foto todavía: tarjeta de texto donde el nombre es el protagonista.
      <div className="flex h-full w-full flex-col justify-between p-7" style={{ backgroundImage: FF_STRIPES }}>
        <p className="font-script text-4xl font-bold leading-none text-white/70">Carlito's</p>
        <p className="font-heading text-[46px] font-black uppercase leading-[1.02] text-white" style={{ ...clampLines(3), textShadow: "0 4px 0 rgba(0,0,0,0.18)" }}>{it.name}</p>
        <div className="flex items-center justify-between">
          {it.popular ? <span className="rounded-full bg-white px-4 py-1.5 font-heading text-base font-black uppercase tracking-wider text-[#1F1574]">★ Favorito</span> : <span />}
          <FFPrice price={it.price} size={40} delay={i * 0.35} />
        </div>
      </div>
    )}
    <div className="ff-shine" style={{ animationDelay: `${1.5 + i * 0.9}s` }} />
  </div>
);

// Columnas × filas según cuántas tarjetas hay, para que 2 o 4 platillos no
// queden amontonados arriba con medio TV vacío.
const ffGrid = (n, vertical) => {
  if (vertical) return n <= 1 ? [1, 1] : n === 2 ? [1, 2] : n <= 4 ? [2, 2] : [2, 3];
  return n <= 3 ? [Math.max(1, n), 1] : n === 4 ? [2, 2] : [3, 2];
};

const BoardFastFood = ({ catName, tagline, items, orientation, pageNo, pageCount }) => {
  const list = items || [];
  const pages = Math.max(1, Math.ceil(list.length / FF_PER_PAGE));
  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
    if (pages <= 1) return undefined;
    const t = setInterval(() => setPage((p) => (p + 1) % pages), 9000);
    return () => clearInterval(t);
  }, [pages, catName]);
  if (!list.length) return <IdleScreen code="" />;
  const vertical = orientation === "vertical";
  const cur = page % pages;
  const shown = list.slice(cur * FF_PER_PAGE, cur * FF_PER_PAGE + FF_PER_PAGE);
  const [cols, rows] = ffGrid(shown.length, vertical);
  const offset = (pageNo || 0) * 2 + cur;
  const dots = pageCount > 1 ? { count: pageCount, active: pageNo } : pages > 1 ? { count: pages, active: cur } : null;
  return (
    <Stage vertical={vertical} style={FF_BG}>
      <style>{FF_CSS}</style>
      <div className="flex h-full w-full flex-col">
        <FFHeader key={catName} catName={catName} tagline={tagline} vertical={vertical} />
        <div
          className={`grid min-h-0 flex-1 gap-6 ${vertical ? "px-12" : "px-14"}`}
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`, paddingBottom: FF_BOTTOM }}
        >
          {shown.map((it, i) => <FFCard key={it.id} it={it} i={i} color={FF_COLORS[(i + offset) % FF_COLORS.length]} />)}
        </div>
      </div>
      {dots && <FFDots count={dots.count} active={dots.active} />}
    </Stage>
  );
};

// Página de "lista de precios" del diseño Comida rápida, para los platillos
// que todavía no tienen foto: barras de colores con nombre y precio.
const BoardFastFoodList = ({ catName, tagline, items, orientation, pageNo, pageCount }) => {
  const vertical = orientation === "vertical";
  const half = Math.ceil(items.length / 2);
  const cols = vertical ? [items] : [items.slice(0, half), items.slice(half)];
  const rows = vertical ? FF_LIST_PER_PAGE.vertical : FF_LIST_PER_PAGE.horizontal / 2;
  const gap = 16;
  const areaH = (vertical ? 1920 : 1080) - FF_HEADER_H[vertical ? "vertical" : "horizontal"] - FF_BOTTOM;
  // Si la página trae menos filas, crecen un poco para llenar el alto (con
  // tope, para que 2 platillos no queden como barras gigantes).
  const fit = (n) => Math.floor((areaH - gap * (n - 1)) / n);
  const used = Math.max(1, ...cols.map((col) => col.length));
  const rowH = Math.min(fit(used), Math.round(fit(rows) * 1.35));
  return (
    <Stage vertical={vertical} style={FF_BG}>
      <style>{FF_CSS}</style>
      <div className="flex h-full w-full flex-col">
        <FFHeader key={catName} catName={catName} tagline={tagline} vertical={vertical} />
        <div className={`flex min-h-0 flex-1 ${vertical ? "px-12" : "px-14"}`}>
          {cols.map((col, c) => (
            <div key={c} className="min-w-0 flex-1" style={{ marginLeft: c ? 28 : 0 }}>
              {col.map((it, r) => (
                <div
                  key={it.id}
                  className="ff-pop relative flex items-center overflow-hidden rounded-[22px] pl-7 pr-4 shadow-xl"
                  style={{ height: rowH, marginTop: r ? gap : 0, backgroundColor: FF_COLORS[(r + c * 3 + (pageNo || 0)) % FF_COLORS.length], backgroundImage: FF_STRIPES, animationDelay: `${(c * half + r) * 0.06}s` }}
                >
                  <p className="min-w-0 flex-1 pr-4 font-heading font-black uppercase text-white" style={{ ...clampLines(2), fontSize: vertical ? 44 : 36, lineHeight: 1.05 }}>{it.name}</p>
                  {it.popular && <span className="mr-3 shrink-0 rounded-full bg-white px-3 py-1 font-heading text-lg font-black text-[#1F1574]">★</span>}
                  <FFPrice price={it.price} size={vertical ? 44 : 38} delay={r * 0.3} />
                  <div className="ff-shine" style={{ animationDelay: `${1 + (c * half + r) * 0.5}s` }} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      {pageCount > 1 && <FFDots count={pageCount} active={pageNo} />}
    </Stage>
  );
};

const Board = ({ design, tag, catName, tagline, items, orientation }) => {
  if (design === "fastfood") return <BoardFastFood catName={catName} tagline={tagline} items={items} orientation={orientation} />;
  if (design === "combo") return <BoardCombo catName={catName} tagline={tagline} items={items} orientation={orientation} />;
  return design === "naranja"
    ? <BoardNaranja tag={tag} catName={catName} tagline={tagline} items={items} orientation={orientation} />
    : <BoardClasico tag={tag} catName={catName} tagline={tagline} items={items} orientation={orientation} />;
};

// Una "promoción" es un grupo de fotos con el mismo nombre, con nombre y
// precio propios (asignados al subirlas) — se muestra con el mismo tablero
// que el menú, no como una foto suelta.
const PromoSlide = ({ group, photos, design, orientation }) => {
  const items = (photos || []).map((p) => ({ id: p.id, name: p.name, image: p.url || `${API}/tv/media/${p.id}/file`, price: p.price }));
  return <Board design={design} tag="Promoción" catName={group || "Promoción"} items={items} orientation={orientation} />;
};

const MenuBoard = ({ menu, branch, categories: onlyCategories, design, orientation }) => {
  const categories = useMemo(() => {
    const groups = {};
    (menu || [])
      .filter((d) => d.available !== false && (!branch || !d.branches || d.branches.length === 0 || d.branches.includes(branch)))
      .filter((d) => !onlyCategories || !onlyCategories.length || onlyCategories.includes(d.category))
      .forEach((d) => { (groups[d.category] = groups[d.category] || []).push(d); });
    // Primero lo fuerte y al final las bebidas, como un menú de verdad.
    const rank = (c) => { const i = CATEGORY_ORDER.indexOf(c); return i < 0 ? CATEGORY_ORDER.length : i; };
    return Object.entries(groups).sort((a, b) => rank(a[0]) - rank(b[0]));
  }, [menu, branch, onlyCategories]);
  const fast = design === "fastfood";
  // En "Comida rápida" cada categoría se parte en páginas: primero tarjetas
  // con foto (la última se completa con tarjetas de texto) y lo que falta en
  // listas de precios. Cada página es una vuelta completa.
  const pages = useMemo(() => {
    if (!fast) return categories.map(([cat, dishes]) => ({ cat, type: "board", items: dishes }));
    const listMax = FF_LIST_PER_PAGE[orientation === "vertical" ? "vertical" : "horizontal"];
    return categories.flatMap(([cat, dishes]) => {
      const withPhoto = dishes.filter((d) => d.image).sort((a, b) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0));
      let rest = dishes.filter((d) => !d.image);
      const out = chunk(withPhoto, FF_PER_PAGE).map((items) => ({ cat, type: "cards", items }));
      const last = out[out.length - 1];
      if (last && last.items.length < FF_PER_PAGE) {
        const fill = FF_PER_PAGE - last.items.length;
        last.items = [...last.items, ...rest.slice(0, fill)];
        rest = rest.slice(fill);
      }
      if (!out.length && rest.length && rest.length <= FF_PER_PAGE) out.push({ cat, type: "cards", items: rest.splice(0) });
      evenChunks(rest, listMax).forEach((items) => out.push({ cat, type: "list", items }));
      return out.map((pg, k) => ({ ...pg, pageNo: k, pageCount: out.length }));
    });
  }, [categories, fast, orientation]);
  const [page, setPage] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPage((p) => p + 1), fast ? 9000 : 6000);
    return () => clearInterval(t);
  }, [fast]);
  if (!pages.length) return <IdleScreen code="" />;
  const pg = pages[page % pages.length];
  const display = CATEGORY_DISPLAY[pg.cat] || { label: pg.cat, tagline: "" };
  if (pg.type === "list") return <BoardFastFoodList catName={display.label} tagline={display.tagline} items={pg.items} orientation={orientation} pageNo={pg.pageNo} pageCount={pg.pageCount} />;
  if (pg.type === "cards") return <BoardFastFood catName={display.label} tagline={display.tagline} items={pg.items} orientation={orientation} pageNo={pg.pageNo} pageCount={pg.pageCount} />;
  return <Board design={design} catName={display.label} tagline={display.tagline} items={pg.items} orientation={orientation} />;
};

// Fotos y videos con otra forma que la del TV (p. ej. un anuncio vertical en
// un TV horizontal) se muestran completos: recortados para llenar la pantalla
// quedan "muy de cerca". El espacio que sobra lleva la marca.
const sameShape = (w, h, frame) => {
  if (!w || !h) return true;
  const r = w / h / (frame.w / frame.h);
  return r > 0.87 && r < 1.15;
};

const BrandPanels = ({ ar, frame }) => {
  const pillar = ar < frame.w / frame.h;
  const size = pillar ? (frame.w - frame.h * ar) / 2 : (frame.h - frame.w / ar) / 2;
  if (size < 90) return null;
  const base = Math.min(size * (pillar ? 0.17 : 0.2), (pillar ? frame.h : frame.w) * 0.09);
  const box = pillar ? { top: 0, width: size, height: "100%" } : { left: 0, width: "100%", height: size };
  return (
    <>
      <div className="absolute flex flex-col items-center justify-center text-center" style={{ ...box, ...(pillar ? { left: 0 } : { top: 0 }) }}>
        <p className="font-script font-bold leading-none text-white" style={{ fontSize: base }}>Carlito's</p>
        <p className="font-heading font-black uppercase text-[#FFD23F]" style={{ fontSize: base * 0.4, letterSpacing: "0.2em", marginTop: base * 0.15 }}>Cuban Food</p>
      </div>
      <div className="absolute flex flex-col items-center justify-center text-center" style={{ ...box, ...(pillar ? { right: 0 } : { bottom: 0 }) }}>
        <p className="font-heading font-black uppercase leading-none text-white" style={{ fontSize: base * 0.62 }}>¡Pídelo<br /><span className="text-[#FFD23F]">hoy!</span></p>
      </div>
    </>
  );
};

const FotoSlide = ({ src }) => {
  const frame = useContext(FrameContext);
  const [dims, setDims] = useState(null);
  const fits = !dims || sameShape(dims.w, dims.h, frame);
  return (
    <div className="relative h-full w-full overflow-hidden" style={fits ? { backgroundColor: "#000" } : FF_BG}>
      <style>{`@keyframes kenburns { from { transform: scale(1); } to { transform: scale(1.15); } } @keyframes kenburnsSoft { from { transform: scale(1); } to { transform: scale(1.04); } }`}</style>
      {!fits && <BrandPanels ar={dims.w / dims.h} frame={frame} />}
      <img
        src={imgUrl(src)}
        alt=""
        onLoad={(e) => setDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
        onError={() => setDims({ w: 0, h: 0 })}
        className="absolute left-0 top-0 h-full w-full"
        style={{ objectFit: fits ? "cover" : "contain", opacity: dims ? 1 : 0, animation: `${fits ? "kenburns" : "kenburnsSoft"} 14s ease-in-out infinite alternate` }}
      />
    </div>
  );
};

const VideoSlide = ({ media, onEnded }) => {
  const frame = useContext(FrameContext);
  const [dims, setDims] = useState(null);
  const fits = !dims || sameShape(dims.w, dims.h, frame);
  return (
    <div className="relative h-full w-full overflow-hidden" style={fits ? { backgroundColor: "#000" } : FF_BG}>
      {!fits && <BrandPanels ar={dims.w / dims.h} frame={frame} />}
      <video
        src={`${API}/tv/media/${media.id}/file`}
        className="absolute left-0 top-0 h-full w-full"
        style={{ objectFit: fits ? "cover" : "contain", opacity: dims ? 1 : 0 }}
        autoPlay
        muted
        playsInline
        onLoadedMetadata={(e) => setDims({ w: e.target.videoWidth, h: e.target.videoHeight })}
        onError={() => setDims({ w: 0, h: 0 })}
        onEnded={onEnded}
      />
    </div>
  );
};

const inWindow = (desde, hasta, now) => {
  const hm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  return hm >= (desde || "00:00") && hm <= (hasta || "23:59");
};

const OfertaSlide = ({ item, orientation }) => {
  const d = item.dish || {};
  const hasWindow = !(item.desde === "00:00" && item.hasta === "23:59");
  const vertical = orientation === "vertical";

  if (vertical) {
    return (
      <div className="flex h-full w-full flex-col bg-gradient-to-b from-brand-navydark via-brand-navy to-brand-navydark">
        {d.image && (
          <div className="flex w-full items-center justify-center px-10 pt-10" style={{ height: "48%" }}>
            <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-[28px] shadow-2xl" style={{ background: "#FFF9F5" }}>
              <img src={imgUrl(d.image)} alt={d.name} className="h-full w-full object-contain p-5" />
            </div>
          </div>
        )}
        <div className="flex-1 px-10 py-8">
          <p className="inline-block rounded-full bg-brand-red px-5 py-2 font-heading text-lg font-extrabold uppercase tracking-widest text-white">{item.label || "Oferta del día"}</p>
          <h2 className="mt-5 font-heading text-5xl font-extrabold leading-none text-white">{d.name}</h2>
          {d.description && <p className="mt-3 text-lg text-white/70">{d.description}</p>}
          <div className="mt-6 flex items-end gap-4">
            <p className="font-heading text-5xl font-extrabold text-brand-red">${Number(item.special_price || d.price).toFixed(2)}</p>
            {item.special_price != null && item.special_price < d.price && (
              <p className="pb-1 font-heading text-2xl font-bold text-white/40 line-through">${d.price.toFixed(2)}</p>
            )}
          </div>
          {hasWindow && <p className="mt-5 text-lg font-semibold text-white/60">Solo hoy de {item.desde} a {item.hasta}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full items-center bg-gradient-to-br from-brand-navydark via-brand-navy to-brand-navydark">
      <div className="flex-1 px-16">
        <p className="inline-block rounded-full bg-brand-red px-6 py-2 font-heading text-2xl font-extrabold uppercase tracking-widest text-white">{item.label || "Oferta del día"}</p>
        <h2 className="mt-6 font-heading text-7xl font-extrabold leading-none text-white">{d.name}</h2>
        {d.description && <p className="mt-4 max-w-xl text-2xl text-white/70">{d.description}</p>}
        <div className="mt-8 flex items-end gap-5">
          <p className="font-heading text-6xl font-extrabold text-brand-red">${Number(item.special_price || d.price).toFixed(2)}</p>
          {item.special_price != null && item.special_price < d.price && (
            <p className="pb-2 font-heading text-3xl font-bold text-white/40 line-through">${d.price.toFixed(2)}</p>
          )}
        </div>
        {hasWindow && <p className="mt-6 text-xl font-semibold text-white/60">Solo hoy de {item.desde} a {item.hasta}</p>}
      </div>
      {d.image && (
        <div className="flex h-full w-[48%] items-center justify-center py-14 pr-14">
          <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-[32px] shadow-2xl" style={{ background: "#FFF9F5" }}>
            <img src={imgUrl(d.image)} alt={d.name} className="h-full w-full object-contain p-8" />
          </div>
        </div>
      )}
    </div>
  );
};

// "Destacados": un platillo estrella a la vez, en grande, con sello de precio
// animado y rayos de luz girando (estilo pantallas de promo de comida rápida).
const DEST_THEMES = [
  { from: "#FF2A2A", to: "#7A0010", ray: "rgba(255,210,63,0.16)" },
  { from: "#3A2BB8", to: "#0A0730", ray: "rgba(255,255,255,0.09)" },
  { from: "#FF8A2A", to: "#9A2D00", ray: "rgba(255,255,255,0.13)" },
];
const BURST_POINTS = Array.from({ length: 48 }, (_, i) => {
  const a = (Math.PI * 2 * i) / 48;
  const r = i % 2 ? 126 : 150;
  return `${(150 + r * Math.cos(a)).toFixed(1)},${(150 + r * Math.sin(a)).toFixed(1)}`;
}).join(" ");
const DEST_CSS = `
@keyframes destSpin { to { transform: rotate(360deg); } }
@keyframes destIn { 0% { opacity: 0; transform: scale(.55) rotate(-12deg); } 70% { opacity: 1; transform: scale(1.04) rotate(2deg); } 100% { opacity: 1; transform: none; } }
@keyframes destSlide { 0% { opacity: 0; transform: translateX(-70px); } 100% { opacity: 1; transform: none; } }
@keyframes destUp { 0% { opacity: 0; transform: translateY(70px); } 100% { opacity: 1; transform: none; } }
@keyframes destBurst { 0% { transform: scale(0) rotate(-40deg); } 70% { transform: scale(1.15) rotate(8deg); } 100% { transform: scale(1) rotate(0); } }
@keyframes destPulse { 0%, 100% { transform: scale(1) rotate(0); } 50% { transform: scale(1.08) rotate(-4deg); } }
@keyframes destZoom { from { transform: scale(1); } to { transform: scale(1.12); } }
.dest-rays { position: absolute; width: 2800px; height: 2800px; margin-left: -1400px; margin-top: -1400px; animation: destSpin 60s linear infinite; }
.dest-in { animation: destIn .9s cubic-bezier(.2,.9,.3,1.2) both; }
.dest-slide { animation: destSlide .7s ease-out both; }
.dest-up { animation: destUp .7s ease-out both; }
.dest-burst { animation: destBurst .8s cubic-bezier(.2,.9,.3,1.3) .45s both; }
.dest-pulse { animation: destPulse 1.8s ease-in-out 1.3s infinite; }
.dest-zoom { animation: destZoom 7s ease-in-out infinite alternate; }
`;

const nameSize = (name, vertical) => {
  const n = (name || "").length;
  const sizes = vertical ? [130, 104, 84, 66] : [150, 120, 96, 76];
  return n <= 8 ? sizes[0] : n <= 14 ? sizes[1] : n <= 22 ? sizes[2] : sizes[3];
};

const DestacadosSlide = ({ items, orientation, label }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (items.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => i + 1), 7000);
    return () => clearInterval(t);
  }, [items.length]);
  const vertical = orientation === "vertical";
  const pos = idx % items.length;
  const d = items[pos];
  const th = DEST_THEMES[pos % DEST_THEMES.length];
  const [whole, cents] = Number(d.price || 0).toFixed(2).split(".");
  const center = vertical ? { left: "50%", top: "33%" } : { left: "70%", top: "50%" };
  const photo = vertical ? 700 : 600;
  return (
    <Stage vertical={vertical} style={{ backgroundColor: th.to, backgroundImage: `radial-gradient(circle at ${center.left} ${center.top}, ${th.from} 0%, ${th.to} 70%)` }}>
      <style>{DEST_CSS}</style>
      <div className="dest-rays" style={{ ...center, backgroundImage: `repeating-conic-gradient(${th.ray} 0deg 9deg, rgba(0,0,0,0) 9deg 18deg)` }} />
      <p className="absolute left-12 top-9 font-script text-5xl font-bold text-white">Carlito's <span className="text-[#FFD23F]">Cuban Food</span></p>

      <div key={idx} className={`relative flex h-full w-full ${vertical ? "flex-col items-center px-14 pt-44" : "items-center pl-24 pr-16"}`}>
        {vertical && (
          <div className="dest-in relative shrink-0" style={{ width: photo, height: photo }}>
            <PhotoCircle d={d} />
            <PriceBurst whole={whole} cents={cents} size={290} style={{ right: -40, bottom: -10 }} />
          </div>
        )}
        <div className={vertical ? "dest-up mt-16 text-center" : "dest-slide w-[46%] shrink-0"}>
          <span className="inline-block rounded-full bg-[#FFD23F] px-6 py-2 font-heading text-2xl font-black uppercase tracking-widest text-[#1F1574]">
            {label || CATEGORY_DISPLAY[d.category]?.label || "Especialidad de la casa"}
          </span>
          <h2 className="mt-6 font-heading font-black uppercase text-white" style={{ fontSize: nameSize(d.name, vertical), lineHeight: 0.92, textShadow: "0 8px 0 rgba(0,0,0,0.22)" }}>{d.name}</h2>
          {d.description && <p className="mt-5 text-2xl leading-snug text-white/85" style={clampLines(3)}>{d.description}</p>}
          <p className="mt-8 font-heading text-3xl font-black uppercase tracking-[0.2em] text-[#FFD23F]">¡Pídelo hoy!</p>
        </div>
        {!vertical && (
          <div className="relative flex flex-1 items-center justify-center">
            <div className="dest-in relative" style={{ width: photo, height: photo }}>
              <PhotoCircle d={d} />
              <PriceBurst whole={whole} cents={cents} size={290} style={{ right: -70, bottom: -20 }} />
            </div>
          </div>
        )}
      </div>

      {items.length > 1 && (
        <div className="absolute bottom-8 left-0 right-0 flex justify-center">
          {items.map((it, k) => (
            <span key={it.id} className="mx-1.5 h-3 rounded-full" style={{ width: k === pos ? 44 : 12, backgroundColor: k === pos ? "#FFD23F" : "rgba(255,255,255,0.35)" }} />
          ))}
        </div>
      )}
    </Stage>
  );
};

const PhotoCircle = ({ d }) => (
  <div className="h-full w-full overflow-hidden rounded-full border-[12px] border-white" style={{ boxShadow: "0 40px 90px rgba(0,0,0,0.45)" }}>
    {d.image ? (
      <img src={imgUrl(d.image)} alt={d.name} className="dest-zoom h-full w-full object-cover" />
    ) : (
      <div className="flex h-full w-full items-center justify-center bg-white/10"><span className="font-script text-7xl font-bold text-white/40">Carlito's</span></div>
    )}
  </div>
);

const PriceBurst = ({ whole, cents, size, style }) => (
  <div className="dest-burst absolute" style={{ width: size, height: size, ...style }}>
    <div className="dest-pulse relative h-full w-full">
      <svg viewBox="0 0 300 300" className="absolute left-0 top-0 h-full w-full" style={{ filter: "drop-shadow(0 14px 22px rgba(0,0,0,0.35))" }}>
        <polygon points={BURST_POINTS} fill="#FFD23F" stroke="#FFFFFF" strokeWidth="7" strokeLinejoin="round" />
      </svg>
      <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center font-heading font-black text-[#1F1574]" style={{ transform: "rotate(-8deg)" }}>
        <span className="self-start pt-[26%] text-[40px] leading-none">$</span>
        <span className="text-[112px] leading-none tracking-tight">{whole}</span>
        <span className="self-start pt-[26%] text-[44px] leading-none">.{cents}</span>
      </div>
    </div>
  </div>
);

export default function TvScreen() {
  const { code } = useParams();
  const [cfg, setCfg] = useState(null);
  const [menu, setMenu] = useState([]);
  const [idx, setIdx] = useState(0);
  const [, setTick] = useState(0); // reevalúa ventanas de horario
  const timer = useRef(null);
  const cfgJson = useRef(null);
  const [vp, setVp] = useState(() => ({ w: window.innerWidth || 1920, h: window.innerHeight || 1080 }));
  const [showName, setShowName] = useState(true);

  const loadCfg = useCallback(() => {
    fetch(`${API}/tv/${code}`).then((r) => (r.ok ? r.json() : Promise.reject())).then((data) => {
      const json = JSON.stringify(data);
      // Evita reemplazar `cfg` por una referencia nueva si el contenido no
      // cambió: `cfg` es dependencia del temporizador que avanza la lista,
      // y el sondeo cada 8s reiniciaría ese temporizador antes de que la
      // mayoría de las duraciones (>8s) lleguen a cumplirse.
      if (json !== cfgJson.current) {
        cfgJson.current = json;
        setCfg(data);
      }
    }).catch(() => {});
  }, [code]);

  useEffect(() => {
    loadCfg();
    fetchMenu().then((d) => setMenu(d.dishes || [])).catch(() => {});
    const cfgT = setInterval(loadCfg, 8000);
    const ping = () => fetch(`${API}/tv/${code}/ping`, { method: "POST" }).catch(() => {});
    ping();
    const pingT = setInterval(ping, 30000);
    const tickT = setInterval(() => setTick((t) => t + 1), 30000);
    return () => { clearInterval(cfgT); clearInterval(pingT); clearInterval(tickT); };
  }, [loadCfg, code]);

  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth || 1920, h: window.innerHeight || 1080 });
    window.addEventListener("resize", onResize);
    // El nombre del TV sale solo al encender (para saber cuál es cuál); el
    // resto del tiempo los clientes no lo ven.
    const t = setTimeout(() => setShowName(false), 20000);
    return () => { window.removeEventListener("resize", onResize); clearTimeout(t); };
  }, []);

  const now = new Date();
  const rawItems = cfg?.playlist?.length ? cfg.playlist : [{ kind: "idle", duration: 9999 }];
  const items = rawItems.filter((i) => (i.kind !== "oferta" && i.kind !== "especial_linea") || inWindow(i.desde, i.hasta, now));
  const list = items.length ? items : [{ kind: "idle", duration: 9999 }];
  const current = list[idx % list.length];

  const next = useCallback(() => setIdx((i) => i + 1), []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (current.kind === "video") {
      timer.current = setTimeout(next, (current.duration || 60) * 3000); // red de seguridad
    } else if (current.kind !== "idle") {
      timer.current = setTimeout(next, (current.duration || 12) * 1000);
    }
    return () => clearTimeout(timer.current);
  }, [idx, cfg]); // eslint-disable-line react-hooks/exhaustive-deps

  const orientation = cfg?.orientation === "vertical" ? "vertical" : "horizontal";
  // TV montado en vertical cuyo navegador sigue dibujando en horizontal: se
  // gira todo 90° (con ?giro=izq en la dirección, hacia el otro lado).
  const rotate = orientation === "vertical" && vp.w > vp.h;
  const frame = rotate ? { w: vp.h, h: vp.w } : vp;
  const frameStyle = rotate
    ? { position: "absolute", left: (vp.w - vp.h) / 2, top: (vp.h - vp.w) / 2, width: vp.h, height: vp.w, transform: `rotate(${/[?&]giro=izq/.test(window.location.search) ? -90 : 90}deg)` }
    : { position: "absolute", left: 0, top: 0, width: "100%", height: "100%" };

  // Precarga en segundo plano TODAS las fotos que puede llegar a mostrar esta
  // pantalla (todo el menú + lo que trae el playlist), no solo la del
  // momento — así cuando le toca el turno a cada una ya está en caché del
  // navegador y no aparece "a medias" mientras carga.
  const preloadUrls = useMemo(() => {
    const urls = new Set();
    menu.forEach((d) => { if (d.image) urls.add(imgUrl(d.image)); });
    (cfg?.playlist || []).forEach((it) => {
      if (it.media) urls.add(it.media.url || `${API}/tv/media/${it.media.id}/file`);
      if (it.photos) it.photos.forEach((p) => urls.add(p.url || `${API}/tv/media/${p.id}/file`));
      if (it.dish?.image) urls.add(imgUrl(it.dish.image));
      if (it.items) it.items.forEach((d) => { if (d.image) urls.add(imgUrl(d.image)); });
    });
    return Array.from(urls);
  }, [cfg, menu]);

  return (
    <div className="fixed inset-0 overflow-hidden bg-black" style={{ cursor: "none" }} data-testid="tv-screen">
      {/* Video de fondo que nunca para — en los TV LG (webOS), el protector
          de pantalla propio del TV (el reloj) no aparece mientras hay un
          video reproduciéndose a pantalla completa, aunque esté tapado por
          el contenido normal. Las animaciones CSS no cuentan para eso. */}
      <video
        src={`${API}/tv/bg-loop.mp4`}
        autoPlay
        loop
        muted
        playsInline
        style={{ position: "fixed", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: -1 }}
      />
      <FrameContext.Provider value={frame}>
      <div style={frameStyle} data-testid="tv-frame">
      {current.kind === "idle" && <IdleScreen code={code} />}
      {current.kind === "promo" && <PromoSlide group={current.group} photos={current.photos} design={current.design} orientation={orientation} />}
      {current.kind === "foto" && current.media && <FotoSlide key={current.media.id} src={current.media.url || `${API}/tv/media/${current.media.id}/file`} />}
      {current.kind === "menu" && <MenuBoard menu={menu} branch={cfg?.branch} categories={current.categories} design={current.design} orientation={orientation} />}
      {current.kind === "video" && current.media && <VideoSlide key={`${idx}-${current.media.id}`} media={current.media} onEnded={next} />}
      {current.kind === "oferta" && current.dish && <OfertaSlide item={current} orientation={orientation} />}
      {current.kind === "especial_linea" && current.dish && (current.design === "fastfood"
        ? <DestacadosSlide items={[current.dish]} label={current.label} orientation={orientation} />
        : <OfertaSlide item={current} orientation={orientation} />)}
      {current.kind === "resto_linea" && current.items && <Board design={current.design} catName="Línea de hoy" tagline="Platillos listos hoy en la línea" items={current.items} orientation={orientation} />}
      {current.kind === "destacados" && current.items?.length > 0 && <DestacadosSlide items={current.items} orientation={orientation} />}
      {showName && (
        <div className="absolute bottom-4 right-6 rounded-full bg-black/50 px-4 py-1 font-heading text-sm font-bold text-white/70">
          {cfg?.name} {cfg?.branch ? `· ${cfg.branch}` : ""}
        </div>
      )}
      </div>
      </FrameContext.Provider>
      {/* Precarga del siguiente video para que los pesados arranquen sin espera */}
      {list[(idx + 1) % list.length]?.kind === "video" && list[(idx + 1) % list.length].media && (
        <video src={`${API}/tv/media/${list[(idx + 1) % list.length].media.id}/file`} preload="auto" muted className="pointer-events-none absolute h-px w-px opacity-0" />
      )}
      {/* Precarga de todas las fotos del menú/playlist en segundo plano, para
          que ya estén en caché cuando le toque el turno a cada una */}
      {preloadUrls.map((u) => (
        <img key={u} src={u} alt="" className="pointer-events-none absolute h-px w-px opacity-0" />
      ))}
    </div>
  );
}
